import { FC, useMemo } from 'react';
import { css } from '@emotion/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { spacing, colors, fontStyles } from '../../../styles';
import { ToolRendererProps, MarketSentimentData } from './types';
import { DataCard } from './shared/DataCard';
import { SENTIMENT_COLORS, SHARED_STYLES } from './shared/constants';

// Types are imported from './types' at the top of the file

const LoadingState: FC<{ message: string }> = ({ message }) => (
  <div
    css={css`
      ${fontStyles.mouse}
      color: ${SENTIMENT_COLORS.muted};
    `}
  >
    {message}
  </div>
);

// Helper functions for sentiment color and emoji mapping
const getSentimentColor = (sentiment: string | number): string => {
  if (typeof sentiment === 'string') {
    const lower = sentiment.toLowerCase();
    if (lower.includes('bullish') || lower.includes('greed')) return SENTIMENT_COLORS.positive;
    if (lower.includes('bearish') || lower.includes('fear')) return SENTIMENT_COLORS.negative;
    if (lower.includes('neutral')) return SENTIMENT_COLORS.neutral;
    return SENTIMENT_COLORS.muted;
  }
  if (typeof sentiment === 'number') {
    if (sentiment >= 60) return SENTIMENT_COLORS.positive;
    if (sentiment <= 40) return SENTIMENT_COLORS.negative;
    return SENTIMENT_COLORS.neutral;
  }
  return SENTIMENT_COLORS.muted;
};

const getSentimentEmoji = (sentiment: string): string => {
  const lower = sentiment.toLowerCase();
  if (lower.includes('bullish') || lower.includes('greed')) return '🟢';
  if (lower.includes('bearish') || lower.includes('fear')) return '🔴';
  return '⚪️';
};

export const MarketSentimentRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  // Handle both string and object outputs with proper typing - must be before early returns
  const sentimentData = useMemo((): MarketSentimentData | null => {
    let data: any = output;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return null;
      }
    }
    // Unwrap market_sentiment wrapper if present
    if (data && data.market_sentiment && typeof data.market_sentiment === 'object') {
      data = data.market_sentiment;
    }
    return data as MarketSentimentData;
  }, [output]);

  // Memoize overall sentiment calculation - must be before early returns
  const overallSentiment = useMemo(() => {
    if (!sentimentData) {
      return { label: 'Neutral ⚪️' };
    }
    const factors = sentimentData.overall_sentiment?.key_factors || [];
    const text = factors.join(' ');

    // Count bearish vs bullish mentions with word boundaries and case-insensitive matching
    const bearishCount = (text.match(/\b(bearish|fear)\b/gi) || []).length;
    const bullishCount = (text.match(/\b(bullish|greed)\b/gi) || []).length;

    if (bearishCount > bullishCount) {
      return { label: 'Bearish 🔴' };
    }
    if (bullishCount > bearishCount) {
      return { label: 'Bullish 🟢' };
    }
    return { label: 'Neutral ⚪️' };
  }, [sentimentData]);

  if (isLoading) {
    return <LoadingState message="Analyzing market sentiment..." />;
  }

  if (!sentimentData) {
    return <LoadingState message="Market sentiment analysis completed" />;
  }

  // Use new cnn_fear_greed or fall back to legacy fear_greed with flexible field access
  const cnnData = sentimentData.cnn_fear_greed || sentimentData.fear_greed;
  const cnnValue = cnnData
    ? ((cnnData as any).score ?? (cnnData as any).index_value ?? (cnnData as any).value ?? null)
    : null;
  const cnnLabel = cnnData
    ? ((cnnData as any).label ??
      (cnnData as any).index_label ??
      (cnnData as any).level ??
      (cnnData as any).rating ??
      null)
    : null;

  return (
    <div
      css={css`
        ${fontStyles.mouse}
        color: ${colors.liquorice};
        padding: ${spacing.deci};
        background: ${colors.fog};
        border-radius: 8px;

        html[data-theme='dark'] & {
          color: ${colors.sugar};
          background: ${colors.liquorice};
        }
      `}
    >
      {/* Header: Overall Sentiment */}
      {sentimentData.overall_sentiment?.key_factors && sentimentData.overall_sentiment.key_factors.length > 0 && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
            padding-bottom: ${spacing.deci};
            border-bottom: 1px solid ${colors.cloud};

            html[data-theme='dark'] & {
              border-bottom-color: ${colors.tungsten};
            }
          `}
        >
          <div
            css={css`
              ${fontStyles.cat}
              font-weight: 600;
              margin-bottom: ${spacing.centi};
            `}
          >
            Market Sentiment: {overallSentiment.label}
          </div>
        </div>
      )}

      {/* Two-column layout: CNN Gauge + AAII Bars */}
      <div
        css={css`
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: ${spacing.deci};
          margin-bottom: ${spacing.deci};

          @media (max-width: 640px) {
            grid-template-columns: 1fr;
          }
        `}
      >
        {/* Left: CNN Fear & Greed Gauge */}
        {cnnData && cnnValue !== null && (
          <DataCard>
            <div css={SHARED_STYLES.sectionHeader}>CNN Fear & Greed</div>
            <div
              css={css`
                text-align: center;
                margin: ${spacing.deci} 0;
              `}
            >
              <div
                css={css`
                  font-size: 36px;
                  font-weight: 700;
                  font-family: monospace;
                  font-variant-numeric: tabular-nums;
                  color: ${getSentimentColor(cnnValue)};
                  margin-bottom: ${spacing.centi};
                `}
              >
                {cnnValue}
              </div>
              <div
                css={css`
                  ${fontStyles.mouse}
                  color: ${SENTIMENT_COLORS.muted};
                  margin-bottom: ${spacing.deci};
                `}
              >
                {cnnLabel}
              </div>
              {/* Visual gauge bar */}
              <div
                css={css`
                  position: relative;
                  height: 8px;
                  background: linear-gradient(
                    to right,
                    ${SENTIMENT_COLORS.negative} 0%,
                    ${SENTIMENT_COLORS.negative} 25%,
                    ${SENTIMENT_COLORS.neutral} 50%,
                    ${SENTIMENT_COLORS.positive} 75%,
                    ${SENTIMENT_COLORS.positive} 100%
                  );
                  border-radius: 4px;
                  margin: ${spacing.centi} 0;
                `}
              >
                <div
                  css={css`
                    position: absolute;
                    top: -4px;
                    left: ${Math.max(2, Math.min(98, cnnValue))}%;
                    transform: translateX(-50%);
                    width: 16px;
                    height: 16px;
                    background: white;
                    border: 3px solid ${getSentimentColor(cnnValue)};
                    border-radius: 50%;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

                    html[data-theme='dark'] & {
                      background: ${colors.liquorice};
                    }
                  `}
                />
              </div>
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  ${fontStyles.flea}
                  color: ${SENTIMENT_COLORS.muted};
                `}
              >
                <span>Extreme Fear</span>
                <span>Neutral</span>
                <span>Extreme Greed</span>
              </div>
            </div>
          </DataCard>
        )}

        {/* Right: AAII Sentiment Bars */}
        {sentimentData.aaii_sentiment &&
          (() => {
            const aaii = sentimentData.aaii_sentiment!;
            const bullishPct = aaii.bullish ?? aaii.bullish_percent ?? 0;
            const bearishPct = aaii.bearish ?? aaii.bearish_percent ?? 0;
            const neutralPct = aaii.neutral ?? aaii.neutral_percent ?? 0;
            return (
              <DataCard>
                <div css={SHARED_STYLES.sectionHeader}>AAII Sentiment Survey</div>
                <div
                  css={css`
                    display: flex;
                    flex-direction: column;
                    gap: ${spacing.centi};
                  `}
                >
                  {/* Bullish bar */}
                  <div>
                    <div
                      css={css`
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: ${spacing.milli};
                        ${fontStyles.flea}
                      `}
                    >
                      <span
                        css={css`
                          color: ${SENTIMENT_COLORS.muted};
                        `}
                      >
                        🟢 Bullish
                      </span>
                      <span
                        css={css`
                          font-weight: 600;
                          font-family: monospace;
                          font-variant-numeric: tabular-nums;
                          color: ${SENTIMENT_COLORS.positive};
                        `}
                      >
                        {Math.round(bullishPct)}%
                      </span>
                    </div>
                    <div
                      role="progressbar"
                      aria-valuenow={Math.round(bullishPct)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label="Bullish sentiment percentage"
                      css={css`
                        height: 8px;
                        background: ${colors.cloud};
                        border-radius: 4px;
                        overflow: hidden;

                        html[data-theme='dark'] & {
                          background: ${colors.tungsten};
                        }
                      `}
                    >
                      <div
                        css={css`
                          height: 100%;
                          width: ${bullishPct}%;
                          background: ${SENTIMENT_COLORS.positive};
                          border-radius: 4px;
                          transition: width 0.3s ease;
                        `}
                      />
                    </div>
                  </div>

                  {/* Bearish bar */}
                  <div>
                    <div
                      css={css`
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: ${spacing.milli};
                        ${fontStyles.flea}
                      `}
                    >
                      <span
                        css={css`
                          color: ${SENTIMENT_COLORS.muted};
                        `}
                      >
                        🔴 Bearish
                      </span>
                      <span
                        css={css`
                          font-weight: 600;
                          font-family: monospace;
                          font-variant-numeric: tabular-nums;
                          color: ${SENTIMENT_COLORS.negative};
                        `}
                      >
                        {Math.round(bearishPct)}%
                      </span>
                    </div>
                    <div
                      role="progressbar"
                      aria-valuenow={Math.round(bearishPct)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label="Bearish sentiment percentage"
                      css={css`
                        height: 8px;
                        background: ${colors.cloud};
                        border-radius: 4px;
                        overflow: hidden;

                        html[data-theme='dark'] & {
                          background: ${colors.tungsten};
                        }
                      `}
                    >
                      <div
                        css={css`
                          height: 100%;
                          width: ${bearishPct}%;
                          background: ${SENTIMENT_COLORS.negative};
                          border-radius: 4px;
                          transition: width 0.3s ease;
                        `}
                      />
                    </div>
                  </div>

                  {/* Neutral bar */}
                  <div>
                    <div
                      css={css`
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: ${spacing.milli};
                        ${fontStyles.flea}
                      `}
                    >
                      <span
                        css={css`
                          color: ${SENTIMENT_COLORS.muted};
                        `}
                      >
                        ⚪️ Neutral
                      </span>
                      <span
                        css={css`
                          font-weight: 600;
                          font-family: monospace;
                          font-variant-numeric: tabular-nums;
                          color: ${SENTIMENT_COLORS.neutral};
                        `}
                      >
                        {Math.round(neutralPct)}%
                      </span>
                    </div>
                    <div
                      role="progressbar"
                      aria-valuenow={Math.round(neutralPct)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label="Neutral sentiment percentage"
                      css={css`
                        height: 8px;
                        background: ${colors.cloud};
                        border-radius: 4px;
                        overflow: hidden;

                        html[data-theme='dark'] & {
                          background: ${colors.tungsten};
                        }
                      `}
                    >
                      <div
                        css={css`
                          height: 100%;
                          width: ${neutralPct}%;
                          background: ${SENTIMENT_COLORS.neutral};
                          border-radius: 4px;
                          transition: width 0.3s ease;
                        `}
                      />
                    </div>
                  </div>
                </div>
              </DataCard>
            );
          })()}
      </div>

      {/* Momentum Analysis Section */}
      {sentimentData.momentum_analysis && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <DataCard>
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.centi};
                margin-bottom: ${spacing.centi};
                flex-wrap: nowrap;
              `}
            >
              <span css={SHARED_STYLES.sectionHeader}>Market Momentum:</span>
              <span
                css={css`
                  font-weight: 600;
                  white-space: nowrap;
                `}
              >
                {sentimentData.momentum_analysis.assessment}{' '}
                {getSentimentEmoji(sentimentData.momentum_analysis.assessment)}
              </span>
            </div>
            <div
              css={css`
                display: flex;
                gap: ${spacing.deci};
                ${fontStyles.flea}
                color: ${SENTIMENT_COLORS.muted};

                @media (max-width: 640px) {
                  flex-direction: column;
                  gap: ${spacing.centi};
                }
              `}
            >
              <div>
                • SPY:{' '}
                {sentimentData.momentum_analysis.spy_diff_pct != null
                  ? `${sentimentData.momentum_analysis.spy_diff_pct > 0 ? '+' : ''}${sentimentData.momentum_analysis.spy_diff_pct.toFixed(1)}% ${sentimentData.momentum_analysis.spy_diff_pct >= 0 ? 'above' : 'below'} 20-day EMA`
                  : sentimentData.momentum_analysis.spy?.price != null &&
                      sentimentData.momentum_analysis.spy?.sma50 != null
                    ? `$${sentimentData.momentum_analysis.spy.price.toFixed(2)} vs SMA50 $${sentimentData.momentum_analysis.spy.sma50.toFixed(2)}`
                    : sentimentData.momentum_analysis.spy_analysis || 'N/A'}
              </div>
              <div>
                • QQQ:{' '}
                {sentimentData.momentum_analysis.qqq_diff_pct != null
                  ? `${sentimentData.momentum_analysis.qqq_diff_pct > 0 ? '+' : ''}${sentimentData.momentum_analysis.qqq_diff_pct.toFixed(1)}% ${sentimentData.momentum_analysis.qqq_diff_pct >= 0 ? 'above' : 'below'} 20-day EMA`
                  : sentimentData.momentum_analysis.qqq?.price != null &&
                      sentimentData.momentum_analysis.qqq?.sma50 != null
                    ? `$${sentimentData.momentum_analysis.qqq.price.toFixed(2)} vs SMA50 $${sentimentData.momentum_analysis.qqq.sma50.toFixed(2)}`
                    : sentimentData.momentum_analysis.qqq_analysis || 'N/A'}
              </div>
            </div>
          </DataCard>
        </div>
      )}

      {/* Volatility Analysis Section */}
      {sentimentData.volatility_analysis && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <DataCard>
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.centi};
                margin-bottom: ${spacing.centi};
                flex-wrap: nowrap;
              `}
            >
              <span
                css={css`
                  ${fontStyles.cat}
                  font-weight: 600;
                `}
              >
                Volatility:
              </span>
              <span
                css={css`
                  font-weight: 600;
                  white-space: nowrap;
                `}
              >
                {sentimentData.volatility_analysis.assessment}{' '}
                {getSentimentEmoji(sentimentData.volatility_analysis.assessment)}
              </span>
            </div>
            <div
              css={css`
                ${fontStyles.flea}
                color: ${SENTIMENT_COLORS.muted};
              `}
            >
              {sentimentData.volatility_analysis.vix?.current != null && (
                <div>
                  • VIX:{' '}
                  <span
                    css={css`
                      font-family: monospace;
                      font-variant-numeric: tabular-nums;
                    `}
                  >
                    {sentimentData.volatility_analysis.vix.current.toFixed(1)}
                  </span>
                </div>
              )}
              {sentimentData.volatility_analysis.vxx_analysis && (
                <div>• {sentimentData.volatility_analysis.vxx_analysis}</div>
              )}
            </div>
          </DataCard>
        </div>
      )}

      {/* AAII Commentary if available */}
      {sentimentData.aaii_sentiment?.commentary && (
        <DataCard>
          <div
            css={css`
              ${fontStyles.flea}
              color: ${SENTIMENT_COLORS.muted};
            `}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ href, children }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    css={css`
                      color: ${SENTIMENT_COLORS.info};
                      text-decoration: underline;
                    `}
                  >
                    {children}
                  </a>
                ),
                p: ({ children }) => (
                  <p
                    css={css`
                      margin: 0;
                    `}
                  >
                    {children}
                  </p>
                ),
              }}
            >
              {sentimentData.aaii_sentiment.commentary.replace(/^AAII Sentiment Survey:\s*/, '')}
            </ReactMarkdown>
          </div>
        </DataCard>
      )}

      {/* Legacy Treasury and Volatility sections for backward compatibility */}
      {sentimentData.treasury && (
        <div
          css={css`
            margin-top: ${spacing.deci};
          `}
        >
          <DataCard>
            <div
              css={css`
                ${fontStyles.cat}
                font-weight: 600;
                margin-bottom: ${spacing.centi};
              `}
            >
              Treasury Analysis
            </div>
            <div css={SHARED_STYLES.cardContainer}>
              <div css={SHARED_STYLES.metricCard}>
                <span css={SHARED_STYLES.metricValue}>{sentimentData.treasury.two_year.toFixed(2)}%</span>
                <div css={SHARED_STYLES.metricLabel}>2Y</div>
              </div>
              <div css={SHARED_STYLES.metricCard}>
                <span css={SHARED_STYLES.metricValue}>{sentimentData.treasury.ten_year.toFixed(2)}%</span>
                <div css={SHARED_STYLES.metricLabel}>10Y</div>
              </div>
              <div css={SHARED_STYLES.metricCard}>
                <span css={SHARED_STYLES.metricValue}>{sentimentData.treasury.thirty_year.toFixed(2)}%</span>
                <div css={SHARED_STYLES.metricLabel}>30Y</div>
              </div>
            </div>
            {sentimentData.treasury.signals && (
              <div
                css={css`
                  ${fontStyles.flea}
                  color: ${SENTIMENT_COLORS.muted};
                  margin-top: ${spacing.centi};
                `}
              >
                {sentimentData.treasury.signals}
              </div>
            )}
          </DataCard>
        </div>
      )}

      {sentimentData.volatility && !sentimentData.volatility_analysis && (
        <div
          css={css`
            margin-top: ${spacing.deci};
          `}
        >
          <DataCard>
            <div
              css={css`
                ${fontStyles.cat}
                font-weight: 600;
                margin-bottom: ${spacing.centi};
              `}
            >
              Volatility (VIX)
            </div>
            <div css={SHARED_STYLES.metricCard}>
              <span
                css={[
                  SHARED_STYLES.metricValue,
                  css`
                    color: ${getSentimentColor(sentimentData.volatility.vix)};
                  `,
                ]}
              >
                {sentimentData.volatility.vix.toFixed(1)}
              </span>
              <div css={SHARED_STYLES.metricLabel}>VIX</div>
            </div>
            {sentimentData.volatility.description && (
              <div
                css={css`
                  ${fontStyles.flea}
                  color: ${SENTIMENT_COLORS.muted};
                  margin-top: ${spacing.centi};
                `}
              >
                {sentimentData.volatility.description}
              </div>
            )}
          </DataCard>
        </div>
      )}
    </div>
  );
};
